import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ShoppingCart, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { Chip, EmptyState } from "@/components/Bits";
import { useBrand } from "@/context/BrandContext";
import { formatINR } from "@/lib/ui";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white transition-colors";
const CHANNELS = ["Brand Website", "Amazon", "Flipkart", "Instagram", "Facebook", "WhatsApp", "Offline", "Phone", "B2B", "Manual Admin Entry"];

function statusChip(s) {
  const g = ["Completed", "Delivered"], y = ["Payment Pending", "Admin Approval", "QC Pending", "On Hold"], r = ["Cancelled", "QC Failed", "Needs Attention", "Returned", "Refund Pending"];
  if (g.includes(s)) return "bg-[#00FF66]/10 border-[#00FF66]/30 text-[#00FF66]";
  if (r.includes(s)) return "bg-[#FF3B30]/10 border-[#FF3B30]/30 text-[#FF6259]";
  if (y.includes(s)) return "bg-[#FFD600]/10 border-[#FFD600]/30 text-[#FFD600]";
  return "bg-[#007AFF]/10 border-[#007AFF]/30 text-[#4DA3FF]";
}

const BLANK_ITEM = { name: "", sku: "", variant: "", qty: 1, unit_price: 0, mrp: 0 };

export default function Orders() {
  const { selectedBrand, brands } = useBrand();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ brand: "", channel: "Instagram", customer_name: "", customer_phone: "", payment_status: "Paid", delivery_address: "", items: [{ ...BLANK_ITEM }] });
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/orders", { params: { brand: selectedBrand } }).then((r) => setOrders(r.data));
  }, [selectedBrand]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    const b = selectedBrand !== "All Brands" ? selectedBrand : (brands[0]?.name || "");
    setForm({ brand: b, channel: "Instagram", customer_name: "", customer_phone: "", payment_status: "Paid", delivery_address: "", items: [{ ...BLANK_ITEM }] });
    if (b) api.get("/products", { params: { brand: b } }).then((r) => setProducts(r.data));
    setOpen(true);
  };

  const setItem = (i, k, v) => setForm((f) => { const items = [...f.items]; items[i] = { ...items[i], [k]: v }; return { ...f, items }; });
  const pickProduct = (i, pid) => {
    const p = products.find((x) => x.id === pid);
    if (p) setItem(i, "name", p.name), setForm((f) => { const items = [...f.items]; items[i] = { ...items[i], product_id: p.id, name: p.name, sku: p.sku, unit_price: p.price, mrp: p.mrp }; return { ...f, items }; });
  };

  const total = form.items.reduce((s, it) => s + (Number(it.unit_price) || 0) * (Number(it.qty) || 0), 0);

  const submit = async () => {
    if (!form.brand || !form.customer_name) return toast.error("Brand and customer required");
    try {
      const items = form.items.filter((it) => it.name).map((it) => ({ ...it, qty: Number(it.qty), unit_price: Number(it.unit_price), mrp: Number(it.mrp) }));
      const { data } = await api.post("/orders", { ...form, items });
      toast.success(`Order ${data.ref} created · tasks auto-generated`);
      setOpen(false);
      load();
      navigate(`/orders/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Orders" subtitle={`Omnichannel OMS · ${selectedBrand}`}
        actions={<button data-testid="create-order-button" onClick={openCreate} className="flex items-center gap-2 text-sm bg-white text-black font-medium px-4 py-2 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98]"><Plus size={16} /> New Order</button>} />
      <div className="p-8">
        {orders.length === 0 ? <EmptyState icon={ShoppingCart} title="No orders yet" hint="Create an order — tasks are generated automatically." /> : (
          <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left border-b border-white/10 bg-[#0a0a0a]">{["Order ID", "Brand", "Channel", "Customer", "Items", "Total", "Stage", "Status"].map((h) => <th key={h} className="py-3 px-3 font-mono text-[11px] uppercase tracking-wider text-zinc-500">{h}</th>)}</tr></thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} data-testid={`order-row-${o.id}`} onClick={() => navigate(`/orders/${o.id}`)} className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors">
                      <td className="py-3 px-3 font-mono text-xs text-[#4DA3FF]">{o.ref}<div className="text-zinc-600">{o.brand_order_id}</div></td>
                      <td className="py-3 px-3 text-zinc-200">{o.brand}</td>
                      <td className="py-3 px-3 text-zinc-400 text-xs">{o.channel}</td>
                      <td className="py-3 px-3 text-zinc-300">{o.customer_name}</td>
                      <td className="py-3 px-3 text-zinc-400">{(o.items || []).length}</td>
                      <td className="py-3 px-3 font-mono text-zinc-200">{formatINR(o.total)}</td>
                      <td className="py-3 px-3 text-zinc-400 text-xs">{o.current_stage}</td>
                      <td className="py-3 px-3"><Chip className={statusChip(o.status)}>{o.status}</Chip></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl max-h-[88vh] overflow-y-auto rounded-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">New Order</DialogTitle><DialogDescription className="sr-only">Create an order; fulfillment tasks are generated automatically from the brand workflow.</DialogDescription></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="order-brand-select" className={inputCls} value={form.brand} onChange={(e) => { setForm((f) => ({ ...f, brand: e.target.value })); api.get("/products", { params: { brand: e.target.value } }).then((r) => setProducts(r.data)); }}>
                <option value="">Brand *</option>{brands.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
              </select>
              <select className={inputCls} value={form.channel} onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))}>{CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}</select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="order-customer-input" className={inputCls} placeholder="Customer name *" value={form.customer_name} onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))} />
              <input className={inputCls} placeholder="Phone" value={form.customer_phone} onChange={(e) => setForm((f) => ({ ...f, customer_phone: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Delivery address" value={form.delivery_address} onChange={(e) => setForm((f) => ({ ...f, delivery_address: e.target.value }))} />
              <select className={inputCls} value={form.payment_status} onChange={(e) => setForm((f) => ({ ...f, payment_status: e.target.value }))}>{["Paid", "Pending", "COD"].map((s) => <option key={s} value={s}>{s}</option>)}</select>
            </div>
            <div className="border-t border-white/10 pt-3">
              <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">Items</div>
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                  <select className={`${inputCls} col-span-5`} value={it.product_id || ""} onChange={(e) => pickProduct(i, e.target.value)}>
                    <option value="">Select product</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
                  </select>
                  <input className={`${inputCls} col-span-3`} placeholder="Variant" value={it.variant} onChange={(e) => setItem(i, "variant", e.target.value)} />
                  <input type="number" className={`${inputCls} col-span-2`} placeholder="Qty" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} />
                  <input type="number" className={`${inputCls} col-span-2`} placeholder="₹" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} />
                </div>
              ))}
              <button onClick={() => setForm((f) => ({ ...f, items: [...f.items, { ...BLANK_ITEM }] }))} className="text-xs font-mono uppercase tracking-wider text-zinc-400 hover:text-white">+ Add item</button>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <span className="text-sm text-zinc-400">Total</span><span className="font-display text-xl font-bold">{formatINR(total)}</span>
            </div>
            <button data-testid="order-submit-button" onClick={submit} className="w-full text-sm bg-white text-black font-medium py-2.5 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98]">Create Order & Generate Tasks</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
